import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.ai.usage');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";


@Component({
    selector: 'wiz-page-ai-usage',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.ai.usage/view.scss */
:host {
  display: block;
  min-height: 100%;
}

.usage-kpi {
  position: relative;
  overflow: hidden;
}

.usage-kpi::after {
  position: absolute;
  right: -2rem;
  bottom: -3.5rem;
  width: 8rem;
  height: 8rem;
  border-radius: 999px;
  background: rgba(14, 165, 233, 0.045);
  content: "";
}

.usage-chart {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.usage-bar-column {
  min-width: 5px;
}

.usage-bar {
  min-height: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .usage-bar {
    transition: none;
  }
}`],
})
export class PageAiUsageComponent implements OnInit {
    public loading: boolean = true;
    public errorMessage: string = '';
    public report: any = this.emptyReport();
    public filters: any = {
        days: 30,
        provider_name: '',
        model_name: ''
    };

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow('/access');
        if (this.service.auth.session?.role !== 'admin') {
            this.service.href('/dashboard');
            return;
        }
        await this.load();
    }

    private emptyReport() {
        return {
            checked_at: '',
            window: { days: 30 },
            summary: {
                requests: 0,
                succeeded: 0,
                failed: 0,
                success_rate: 0,
                priced_requests: 0,
                unpriced_requests: 0,
                pricing_coverage: 100,
                input_tokens: 0,
                output_tokens: 0,
                total_tokens: 0,
                cost_micros: 0,
                average_cost_micros: 0,
                cost_change_pct: 0,
                request_change_pct: 0
            },
            budget: {
                configured: false,
                limit_micros: 0,
                spent_micros: 0,
                remaining_micros: 0,
                usage_pct: 0,
                exceeded: false
            },
            models: [],
            daily: [],
            recent: [],
            options: { providers: [], models: [] },
            pricing: { effective_date: '', estimated: true }
        };
    }

    public async load() {
        if (this.loading && this.report?.checked_at) return;
        this.loading = true;
        this.errorMessage = '';
        await this.service.render();
        try {
            const { code, data } = await wiz.call('overview', this.filters);
            if (code !== 200 || !data?.report) {
                this.errorMessage = data?.message || 'AI 비용 현황을 불러오지 못했습니다.';
                return;
            }
            this.report = data.report;
        } catch (error) {
            this.errorMessage = 'AI 비용 현황을 불러오지 못했습니다.';
        } finally {
            this.loading = false;
            await this.service.render();
        }
    }

    public async resetFilters() {
        this.filters = { days: 30, provider_name: '', model_name: '' };
        await this.load();
    }

    public formatUsd(micros: any) {
        const value = Math.max(0, Number(micros || 0)) / 1_000_000;
        return value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: value > 0 && value < 0.01 ? 4 : 2,
            maximumFractionDigits: value > 0 && value < 0.01 ? 4 : 2
        });
    }

    public formatNumber(value: any) {
        return Math.max(0, Number(value || 0)).toLocaleString('ko-KR');
    }

    public compactNumber(value: any) {
        return new Intl.NumberFormat('ko-KR', {
            notation: 'compact',
            maximumFractionDigits: 1
        }).format(Math.max(0, Number(value || 0)));
    }

    public trendLabel(value: any) {
        if (value === null || value === undefined) return '이전 기간 신규';
        const number = Number(value || 0);
        if (number === 0) return '이전 기간과 동일';
        return `이전 기간 대비 ${number > 0 ? '+' : ''}${number.toFixed(1)}%`;
    }

    public trendClass(value: any) {
        if (value === null || value === undefined) return 'text-sky-700';
        const number = Number(value || 0);
        if (number > 0) return 'text-amber-700';
        if (number < 0) return 'text-emerald-700';
        return 'text-gray-500';
    }

    public chartMax() {
        return Math.max(1, ...(this.report?.daily || []).map((item: any) => Number(item.cost_micros || 0)));
    }

    public barHeight(value: any) {
        const cost = Math.max(0, Number(value || 0));
        if (!cost) return 2;
        return Math.max(8, Math.round((cost / this.chartMax()) * 100));
    }

    public barWidth() {
        const days = Number(this.report?.window?.days || 30);
        if (days <= 7) return 54;
        if (days <= 30) return 20;
        return 9;
    }

    public budgetWidth() {
        return Math.min(100, Math.max(0, Number(this.report?.budget?.usage_pct || 0)));
    }

    public shortDate(value: any) {
        const text = String(value || '');
        return text.length >= 10 ? text.slice(5, 10).replace('-', '.') : '-';
    }

    public dateTime(value: any) {
        return String(value || '').replace('T', ' ').replace('Z', '').slice(0, 16) || '-';
    }

    public operationLabel(value: string) {
        const labels: any = {
            statement_structure: '환자 진술 정리',
            treatment_scenario: '치료 시나리오',
            prompt_draft: '프롬프트 초안',
            prompt_translation: '한국어 참고 번역',
            prompt_render: '생성용 영문 프롬프트',
            image_generation: '이미지 생성',
            audio_generation: '오디오 생성',
            video_generation: '영상 생성'
        };
        return labels[value] || value || '-';
    }

    public pricingLabel(value: string) {
        const labels: any = {
            configured: '요금 적용',
            provider_reported: 'Provider 산정',
            no_charge: '내부/무료',
            unpriced: '요금 미설정',
            unpriced_legacy: '과거 요금 미확인'
        };
        return labels[value] || value || '-';
    }

    public pricingClass(value: string) {
        if (value === 'unpriced' || value === 'unpriced_legacy') {
            return 'border-amber-200 bg-amber-50 text-amber-700';
        }
        if (value === 'no_charge') return 'border-gray-200 bg-gray-50 text-gray-600';
        return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    }
}

export default PageAiUsageComponent;